import React from "react";
import GlobalStyles from "@mui/material/GlobalStyles";

declare global {
  interface Window {
    zE?: (...args: unknown[]) => void;
  }
}

/**
 * Zendesk's launcher bubble (`iframe#launcher`) and unread-count badge
 * (`iframe[title="Number of unread messages"]`) are separate iframes Zendesk
 * injects directly under <body>, outside this app's tree, so they can't be
 * shown/hidden via ordinary React/CSS composition — this targets their real
 * DOM by selector instead. `visibility` (not `display`) is used so two
 * instances of this component can cleanly override one another by source
 * order (later-mounted wins, since both rules share the same selector and
 * !important): App.tsx renders this hidden everywhere by default, and
 * MainLayout renders it visible again for authenticated pages.
 */
export const ZendeskWidgetVisibility: React.FC<ZendeskWidgetVisibilityProps> = ({ visible }) => {
  const visibility = `${visible ? "visible" : "hidden"} !important`;

  // Hiding the launcher doesn't close an already-open conversation panel — that's a
  // separate iframe Zendesk manages itself, and Zendesk persists its open/closed state
  // across page loads (sessionStorage) so a refreshed page reopens a chat left open.
  // Logout goes through Auth0's hard redirect (useLogout -> auth0Logout), not a same-tab
  // route change, so there's no React unmount to hook into — the app (and any open chat)
  // just gets torn down and reloaded fresh on /login. Closing here, once on mount, only
  // when this instance starts out hidden (App.tsx's always-mounted visible={false}, which
  // is exactly what renders on that fresh /login load) force-closes any stale open state
  // left over from before logout. Deliberately mount-only (not reactive to `visible`), so
  // resizing to mobile width while a chat is genuinely in progress (MainLayout's own
  // visible={!isMobile} toggle on an already-mounted instance) doesn't kill it mid-conversation.
  React.useEffect(() => {
    if (visible) {
      return undefined;
    }

    // The Zendesk snippet (public/index.html) loads via a plain `async` <script>, with no
    // synchronous stub/queue like some widgets provide — `window.zE` may not exist yet at
    // mount, so a single check-and-close here can silently no-op. Poll instead, with no
    // cutoff: a stale, unclosed conversation left exposed on the login screen is worse than
    // an indefinite `typeof` check, which costs nothing while it waits (and stops the moment
    // window.zE appears, or the page unloads if the script never arrives at all, e.g. an
    // ad blocker).
    const POLL_INTERVAL_MS = 200;

    const intervalId = window.setInterval(() => {
      if (typeof window.zE === "function") {
        window.zE("messenger", "close");
        window.clearInterval(intervalId);
      }
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <GlobalStyles
      styles={{
        "iframe#launcher": { visibility },
        'iframe[title="Number of unread messages"]': { visibility },
      }}
    />
  );
};

export interface ZendeskWidgetVisibilityProps {
  visible: boolean;
}

export default ZendeskWidgetVisibility;
